import { Button, DrawerCloseButton, Flex, Text } from '@chakra-ui/react';
import type { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { useQuery } from '@tanstack/react-query';
import Balance from '@ui/components/Balance';
import Info from '@ui/components/Info';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBodyWithMask,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer';
import type { NftToken } from '@ui/components/NFTCard';
import type { TransactionStatus } from '@ui/components/TransactionDrawer';
import {
  TransactionError,
  TransactionPending,
  UnstakeTransactionCompleted,
} from '@ui/components/TransactionDrawer';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import useRequestUnbond from '@ui/hooks/useRequestUnbond';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { getNftTokens } from '@ui/queries/sdk';
import { unstakePeriodDict } from '@ui/utils/unstake/periodDict';
import type { EthersError } from '@veno-app/sdk';
import { currentWallet } from '@veno-app/wallet';
import format from 'date-fns/format';
import { useCallback, useState } from 'react';

const { useAccount } = currentWallet;

interface UnstakeCroDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unstakeAmount: string;
  unbondUnlockDate?: BigNumber;
  croToUnlock: string;
  coinMode: CoinMode;
}
const UnstakeCroDrawer: React.FC<UnstakeCroDrawerProps> = ({
  isOpen,
  onClose,
  unstakeAmount,
  unbondUnlockDate,
  croToUnlock,
  coinMode,
}) => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const account = useAccount();
  const [transactionStatus, setTransactionStatus] =
    useState<TransactionStatus>('CONFIRM');
  const [txHash, setTxHash] = useState<string>('');
  const [error, setError] = useState<EthersError | null>(null);

  const { mutateAsync: requestUnbond } = useRequestUnbond();

  const sdk = useVenoSDK();
  const getNftTokensQuery = getNftTokens({
    coinMode: coinMode,
    sdk,
    owner: account ?? '',
    latest: true,
  });
  const { data: nftTokens, isLoading: isNftsTokenLoading } = useQuery(
    getNftTokensQuery.queryKey,
    getNftTokensQuery.queryFn,
    {
      enabled: Boolean(account) && transactionStatus === 'COMPLETED',
    },
  );
  const latestNftToken = nftTokens ? nftTokens[0] : null;

  const handleRequestUnbond = useCallback(async () => {
    if (!account) return;
    try {
      setTransactionStatus('PENDING');
      setError(null);
      const tx = await requestUnbond({
        receiver: account,
        amount: parseUnits(unstakeAmount, coinModeInfo.liquidDecimals),
      });
      setTxHash(tx.txHash);
      const p = await tx.txReceiptPromise;
      if (p.blockNumber) setTransactionStatus('COMPLETED');
    } catch (err) {
      setError(err as EthersError);
      console.error(err);
      setTransactionStatus('ERROR');
    }
  }, [account, requestUnbond, unstakeAmount, coinModeInfo.liquidDecimals]);

  const handleCloseAndReset = (): void => {
    onClose();
    setTransactionStatus('CONFIRM');
    setError(null);
  };
  return (
    <>
      <ModalOrDrawer
        isOpen={isOpen}
        onClose={handleCloseAndReset}
        scrollBehavior="inside"
        placement="bottom"
        size={{
          base: 'full',
          desktop: 'md',
        }}
      >
        <ModalOrDrawerOverlay />
        <ModalOrDragDrawerContent>
          <ModalOrDrawerHeader>
            {t('Unstake {coin}', {
              coin: coinModeInfo.name,
            })}
          </ModalOrDrawerHeader>
          <InDesktop>
            <DrawerCloseButton />
          </InDesktop>
          <ModalOrDrawerBodyWithMask>
            <UnstakeCroContent
              coinMode={coinMode}
              account={account}
              unstakeAmount={unstakeAmount}
              croToUnlock={croToUnlock}
              txHash={txHash}
              error={error}
              transactionStatus={transactionStatus}
              unbondUnlockDate={unbondUnlockDate}
              isNftsTokenLoading={isNftsTokenLoading}
              latestNftToken={latestNftToken}
            />
          </ModalOrDrawerBodyWithMask>
          <ModalOrDrawerFooter>
            <UnstakeCroFooter
              handleCloseAndReset={handleCloseAndReset}
              transactionStatus={transactionStatus}
              handleRequestUnbond={handleRequestUnbond}
              latestNftTokenId={latestNftToken?.id.toString() ?? null}
            />
          </ModalOrDrawerFooter>
        </ModalOrDragDrawerContent>
      </ModalOrDrawer>
    </>
  );
};

type UnstakeCroContentProps = {
  unstakeAmount: string;
  unbondUnlockDate?: BigNumber;
  croToUnlock: string;
  account?: string;
  txHash: string;
  error: EthersError | null;
  transactionStatus: TransactionStatus;
  coinMode: CoinMode;
  isNftsTokenLoading: boolean;
  latestNftToken: NftToken | null;
};

const UnstakeCroContent = ({
  unbondUnlockDate,
  croToUnlock,
  txHash,
  unstakeAmount,
  error,
  transactionStatus,
  isNftsTokenLoading,
  latestNftToken,
  coinMode,
}: UnstakeCroContentProps) => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const periodInfo = unstakePeriodDict[coinMode];

  return (() => {
    switch (transactionStatus) {
      case 'COMPLETED':
        return (
          <UnstakeTransactionCompleted.Content
            coinMode={coinMode}
            isNftsTokenLoading={isNftsTokenLoading}
            latestNftToken={latestNftToken}
            txHash={txHash}
          />
        );
      case 'PENDING':
        return <TransactionPending.Content />;
      case 'ERROR':
        return <TransactionError.Content error={error} />;
      default:
        return (
          <>
            <Balance
              mb={6}
              title={t('Amount to unstake')}
              amount={parseFloat(unstakeAmount)}
              icon={<coinModeInfo.LiquidIcon />}
            />
            <Text textStyle="body" color="text.light">
              {t(
                'unstake-drawer-desc-{minDay}-{maxDay}-{coin}-{liquidCoin}-{br}',
                {
                  minDay: periodInfo.unbondingDays,
                  maxDay: periodInfo.unbondingDays + periodInfo.batchDays,
                  coin: coinModeInfo.name,
                  liquidCoin: coinModeInfo.liquidName,
                  br: () => <br />,
                },
              )}
            </Text>
            <Flex mt={6} gap={4} py={1} flexDir="column">
              <Info
                title={t('{coin} to unlock', {
                  coin: coinModeInfo.name,
                })}
                value={croToUnlock}
              />
              <Info title={t('Withdrawal Fee')} value="0.2%" />
              {unbondUnlockDate !== undefined && (
                <Info
                  title={t('Expected unlock date')}
                  value={format(
                    unbondUnlockDate.toNumber() * 1000,
                    'dd MMM yyyy',
                  )}
                />
              )}
            </Flex>
          </>
        );
    }
  })();
};

type UnstakeCroFooterProps = {
  handleCloseAndReset: () => void;
  transactionStatus: TransactionStatus;
  handleRequestUnbond: () => void;
  latestNftTokenId: string | null;
};
const UnstakeCroFooter: React.FC<UnstakeCroFooterProps> = ({
  handleCloseAndReset,
  transactionStatus,
  handleRequestUnbond,
  latestNftTokenId,
}) => {
  switch (transactionStatus) {
    case 'COMPLETED':
      return (
        <UnstakeTransactionCompleted.Footer
          onCloseDrawer={handleCloseAndReset}
          latestNftTokenId={latestNftTokenId}
        />
      );
    case 'PENDING':
      return <TransactionPending.Footer />;
    case 'ERROR':
      return null;
    default:
      return <Button onClick={handleRequestUnbond}>Confirm unstaking</Button>;
  }
};

export default UnstakeCroDrawer;
